import { FormControl, FormGroup } from '@angular/forms';
import {
  ColumnPipeTypes,
  IHowdenColumnConfig,
  ITableButtonConfig,
  ITableEventData
} from '@howdeniberia/core-front';
import { ISearchRequest } from 'src/app/core/models/api/sane-project/arpa-model';
import { toISODateOnly } from 'src/app/shared/utils';
import { SearchResultViewModel } from './sane-project-search-result.viewmodel';

export class SearchViewModel {
  private _filterForm: FormGroup;
  private _request: ISearchRequest;
  private _length = 0;

  public readonly C_PROJECT_DATE_FROM = 'projectDateFrom';
  public readonly C_PROJECT_DATE_TO = 'projectDateTo';
  public readonly C_PROJECT_DATE = 'projectDate';
  public readonly C_PROJECT_NAME = 'projectName';
  public readonly C_TOTAL_BONUS = 'totalBonus';
  public readonly C_CLIENT_DESC = 'clientDescription';
  public readonly C_CLIENT_PROFESSION = 'clientProfession';
  public readonly C_CLIENT_AGE = 'clientAge';
  public readonly C_USER_DESC = 'userDescription';
  public readonly C_OFFICE_DESC = 'officeDescription';

  constructor() {
    const fromDate = new Date();
    fromDate.setMonth(fromDate.getMonth() - 1);

    this._filterForm = new FormGroup({
      [this.C_PROJECT_DATE_FROM]: new FormControl(fromDate),
      [this.C_PROJECT_DATE_TO]: new FormControl(''),
      [this.C_PROJECT_NAME]: new FormControl(''),
      [this.C_CLIENT_DESC]: new FormControl(''),
      [this.C_USER_DESC]: new FormControl(''),
      [this.C_OFFICE_DESC]: new FormControl('')
    });

    this._request = {
      pageNumber: 0,
      pageSize: 10,
      sortedBy: '',
      sortDirection: '',
      loggedUserId: ''
    } as ISearchRequest;

    this.updateServerSideFilters();
  }

  data: SearchResultViewModel[] = [];

  columns: IHowdenColumnConfig[] = [
    {
      fieldName: this.C_PROJECT_DATE,
      fieldHeader: $localize`:@@app.sane-projects.search.projectDate:Fecha`,
      canSort: true,
      pipeToApply: ColumnPipeTypes.DATE,
      width: '8%'
    },
    {
      fieldName: this.C_PROJECT_NAME,
      fieldHeader: $localize`:@@app.sane-projects.search.projectNameShort:Nombre`,
      canSort: true,
      width: '20%'
    },
    {
      fieldName: this.C_TOTAL_BONUS,
      fieldHeader: $localize`:@@app.sane-projects.search.totalBonus:Bonus`,
      canSort: true,
      pipeToApply: ColumnPipeTypes.DECIMAL,
      width: '8%'
    },
    {
      fieldName: this.C_CLIENT_DESC,
      fieldHeader: $localize`:@@app.sane-projects.search.client:Cliente`,
      canSort: true,
      width: '18%'
    },
    {
      fieldName: this.C_CLIENT_PROFESSION,
      fieldHeader: $localize`:@@app.sane-projects.search.clientProfession:Profesión`,
      canSort: true,
      width: '12%'
    },
    {
      fieldName: this.C_CLIENT_AGE,
      fieldHeader: $localize`:@@app.sane-projects.search.clientAge:Edad`,
      canSort: true,
      width: '5%'
    },
    {
      fieldName: this.C_OFFICE_DESC,
      fieldHeader: $localize`:@@app.sane-projects.search.office:Oficina`,
      canSort: true,
      width: '8%'
    },
    {
      fieldName: this.C_USER_DESC,
      fieldHeader: $localize`:@@app.sane-projects.search.user:Usuario`,
      canSort: true,
      width: '12%'
    }
  ];

  buttons: ITableButtonConfig[] = [
    { name: 'download-report', icon: 'download', tooltip: $localize`:@@app.sane-projects.search.actions.download-report.tooltip:Descargar Informe` },
    { name: 'sign-project', icon: 'draw', tooltip: $localize`:@@app.sane-projects.search.actions.sign.tooltip:Firmar Programa de Previsión Social Individual` }
  ];

  get length(): number { return this._length; }
  set length(len: number) { this._length = len; }
  public get request(): ISearchRequest { return this._request; }
  public get filterForm(): FormGroup { return this._filterForm; }
  public set filterForm(f: FormGroup) { this._filterForm = f; }

  get projectDateFrom(): unknown {
    return this._filterForm.get(this.C_PROJECT_DATE_FROM)?.value;
  }
  set projectDateFrom(v: unknown) {
    this._filterForm.get(this.C_PROJECT_DATE_FROM)?.setValue(v);
  }

  get projectDateTo(): unknown {
    return this._filterForm.get(this.C_PROJECT_DATE_TO)?.value;
  }
  set projectDateTo(v: unknown) {
    this._filterForm.get(this.C_PROJECT_DATE_TO)?.setValue(v);
  }

  get projectName(): string | undefined {
    return this._filterForm.get(this.C_PROJECT_NAME)?.value;
  }
  set projectName(v: string | undefined) {
    this._filterForm.get(this.C_PROJECT_NAME)?.setValue(v);
  }

  get clientDescription(): string | undefined {
    return this._filterForm.get(this.C_CLIENT_DESC)?.value;
  }
  set clientDescription(v: string | undefined) {
    this._filterForm.get(this.C_CLIENT_DESC)?.setValue(v);
  }

  get officeDescription(): string | undefined {
    return this._filterForm.get(this.C_OFFICE_DESC)?.value;
  }
  set officeDescription(v: string | undefined) {
    this._filterForm.get(this.C_OFFICE_DESC)?.setValue(v);
  }

  get userDescription(): string | undefined {
    return this._filterForm.get(this.C_USER_DESC)?.value;
  }
  set userDescription(v: string | undefined) {
    this._filterForm.get(this.C_USER_DESC)?.setValue(v);
  }

  updateServerSideConfig(event: ITableEventData): void {
    this.request.pageNumber = event.pageIndex;
    this.request.pageSize = event.pageSize;
    this.request.sortedBy = event.fieldSorted ?? '';
    this.request.sortDirection = (event.order ?? '').toUpperCase();

    this.updateServerSideFilters();
  }

  updateServerSideFilters(): void {
    this.request.projectDateFrom = toISODateOnly(this.projectDateFrom);
    this.request.projectDateTo = toISODateOnly(this.projectDateTo);
    this.request.projectName = (this.projectName ?? '').trim();
    this.request.clientDescription = (this.clientDescription ?? '').trim();
    this.request.officeDescription = (this.officeDescription ?? '').trim();
    this.request.userDescription = (this.userDescription ?? '').trim();
  }
}
